import { makeDownstreamApiCall, Response as DownstreamResponse } from "@swiggy-private/checkout-core";

import { JuspayClientAuthTokenData } from "@payment-client/interfaces/Juspay";

// ─── Endpoint ─────────────────────────────────────────────────────────────────

const ENDPOINT = {
    GET_CUSTOMER_AUTH_TOKENS: "/api/v3/juspay/get-customer-auth-token",
};

const MAX_RETRIES = 2;

// ─── Types ────────────────────────────────────────────────────────────────────

interface AuthTokensV3ResponseData {
    // camelCased by makeDownstreamApiCall
    authTokens: Record<string, JuspayClientAuthTokenData>;
}

export interface GetAuthTokensV3Params {
    apiHost: string;
    /** Comma-separated tenant keys, e.g. "CHECKOUT" or "CHECKOUT,DASH" */
    tenants: string;
}

// ─── API ──────────────────────────────────────────────────────────────────────

/**
 * Fetches Juspay V3 auth tokens for the requested tenants.
 * Retries up to MAX_RETRIES times if statusCode is not 0.
 * Returns a map of tenantKey → token data (empty object on full failure).
 */
export async function getPublicJuspayClientAuthTokensV3(
    params: GetAuthTokensV3Params,
): Promise<Record<string, JuspayClientAuthTokenData>> {
    const { apiHost, tenants } = params;

    let response: DownstreamResponse<AuthTokensV3ResponseData> | undefined;
    let attempts = 0;

    do {
        response = await makeDownstreamApiCall<AuthTokensV3ResponseData>({
            apiHost,
            endpoint: ENDPOINT.GET_CUSTOMER_AUTH_TOKENS,
            options: {
                headers: { tenants },
            },
        });
        attempts++;
    } while (response?.statusCode !== 0 && attempts <= MAX_RETRIES);

    return response?.data?.authTokens ?? {};
}
